import { provideHttpClient, withInterceptors } from '@angular/common/http';
import {
  ApplicationConfig,
  provideAppInitializer,
  provideBrowserGlobalErrorListeners,
} from '@angular/core';
import { MAT_DATE_LOCALE, provideNativeDateAdapter } from '@angular/material/core';
import { MAT_FORM_FIELD_DEFAULT_OPTIONS } from '@angular/material/form-field';
import { bootstrapApplication } from '@angular/platform-browser';
import { provideRouter, Routes, withInMemoryScrolling } from '@angular/router';
import { NotFoundComponent } from '@components/404/404.component';
import { AboutComponent } from '@components/about/about.component';
import { AccountPasswordComponent } from '@components/account/password/password.component';
import { ApplicationFormComponent } from '@components/application/form/form.component';
import { ApplicationListComponent } from '@components/application/list/list.component';
import { AttachmentListComponent } from '@components/attachment/list/list.component';
import { DatasetFormComponent } from '@components/dataset/form/form.component';
import { DatasetItemComponent } from '@components/dataset/item/item.component';
import { DatasetListComponent } from '@components/dataset/list/list.component';
import { DatasetMetadataComponent } from '@components/dataset/metadata/metadata.component';
import { GeologicalSampleFormComponent } from '@components/geology/sample/form/form.component';
import { GeologicalSampleItemComponent } from '@components/geology/sample/item/item.component';
import { GeologicalSampleListComponent } from '@components/geology/sample/list/list.component';
import { LabelFormComponent } from '@components/label/form/form.component';
import { LabelListComponent } from '@components/label/list/list.component';
import { MapArchiveStepperComponent } from '@components/map/archive/form/stepper.component';
import { MapArchiveItemComponent } from '@components/map/archive/item/item.component';
import { MapArchiveListComponent } from '@components/map/archive/list/list.component';
import { PermissionListComponent } from '@components/permission/list/list.component';
import { CaseFormComponent } from '@components/placename/case/form/form.component';
import { CaseItemComponent } from '@components/placename/case/item/item.component';
import { CaseListComponent } from '@components/placename/case/list/list.component';
import { PlacenameFormComponent } from '@components/placename/form/form.component';
import { PlacenameItemComponent } from '@components/placename/item/item.component';
import { PlacenameListComponent } from '@components/placename/list/list.component';
import { PlanktonEventItemComponent } from '@components/plankton/event/item/item.component';
import { PlanktonEventListComponent } from '@components/plankton/event/list/list.component';
import { PlanktonExpeditionListComponent } from '@components/plankton/expedition/list/list.component';
import { PlanktonOccurrenceItemComponent } from '@components/plankton/occurrence/item/item.component';
import { PlanktonOccurrenceListComponent } from '@components/plankton/occurrence/list/list.component';
import { PlanktonOriginItemComponent } from '@components/plankton/origin/item/item.component';
import { PlanktonOriginListComponent } from '@components/plankton/origin/list/list.component';
import { PlanktonTaxonItemComponent } from '@components/plankton/taxon/item/item.component';
import { PlanktonTaxonListComponent } from '@components/plankton/taxon/list/list.component';
import { RecordListComponent } from '@components/record/list/list.component';
import { RoleFormComponent } from '@components/role/form/form.component';
import { RoleListComponent } from '@components/role/list/list.component';
import { RootComponent } from '@components/root/root.component';
import { UserListComponent } from '@components/user/list/list.component';
import { VesselItemComponent } from '@components/vessel/item/item.component';
import { VesselListComponent } from '@components/vessel/list/list.component';
import { VesselStepperFormComponent } from '@components/vessel/stepper/form.component';
import { KPHLiveComponent } from '@components/live/kph/kph.component';
import { hasTokenParamGuard, isAdminGuard, isLoggedInGuard, isNotLoggedInGuard } from '@guards';
import { authorizationInterceptor } from '@interceptors';
import {
  applicationResolver,
  caseResolver,
  datasetResolver,
  geologicalSampleResolver,
  labelResolver,
  mapArchiveResolver,
  placenameResolver,
  planktonEventResolver,
  planktonOccurrenceResolver,
  planktonOriginResolver,
  planktonTaxonResolver,
  roleResolver,
  vesselResolver,
  datasetPermissionResolver,
} from '@resolvers';
import { GeodataComponent } from '@components/geodata/geodata.component';
import { DATE_PIPE_DEFAULT_OPTIONS } from '@angular/common';
import { Map } from 'leaflet';
import { environment } from '@environment';

const routes: Routes = [
  { path: '', redirectTo: '/dataset', pathMatch: 'full' },
  {
    path: 'dataset',
    children: [
      {
        path: '',
        title: 'Datasets - NPDC',
        component: DatasetListComponent,
      },
      {
        path: 'new',
        component: DatasetFormComponent,
        canActivate: [isLoggedInGuard],
      },
      {
        path: ':id',
        children: [
          {
            path: '',
            component: DatasetItemComponent,
            resolve: {
              dataset: datasetResolver,
              permissions: datasetPermissionResolver,
            },
            children: [
              {
                path: '',
                component: DatasetMetadataComponent,
              },
              {
                path: 'file',
                component: AttachmentListComponent,
              },
              {
                path: 'record',
                component: RecordListComponent,
              },
            ],
          },
          {
            path: 'edit',
            component: DatasetFormComponent,
            resolve: {
              dataset: datasetResolver,
            },
            canActivate: [isLoggedInGuard],
          },
          {
            path: 'permission',
            component: PermissionListComponent,
            resolve: { dataset: datasetResolver },
            canActivate: [isLoggedInGuard],
          },
        ],
      },
    ],
  },
  {
    path: 'geology',
    children: [
      {
        path: '',
        pathMatch: 'full',
        redirectTo: '/geology/sample',
      },
      {
        path: 'sample',
        title: 'Geological Sample Archive - NPDC',
        children: [
          {
            path: '',
            resolve: { permissions: datasetPermissionResolver },
            component: GeologicalSampleListComponent,
            data: { datasetId: environment.geologicalSampleDatasetId },
          },
          {
            path: ':id',
            children: [
              {
                path: '',
                component: GeologicalSampleItemComponent,
                resolve: {
                  permissions: datasetPermissionResolver,
                  sample: geologicalSampleResolver,
                },
                data: { datasetId: environment.geologicalSampleDatasetId },
              },
              {
                path: 'edit',
                component: GeologicalSampleFormComponent,
                canActivate: [isLoggedInGuard],
                resolve: {
                  permissions: datasetPermissionResolver,
                  sample: geologicalSampleResolver,
                },
                data: { datasetId: environment.geologicalSampleDatasetId },
              },
            ],
          },
        ],
      },
    ],
  },
  {
    path: 'plankton',
    children: [
      {
        path: '',
        pathMatch: 'full',
        redirectTo: '/plankton/expedition',
      },
      {
        path: 'expedition',
        title: 'Plankton expeditions - NPDC',
        component: PlanktonExpeditionListComponent,
      },
      {
        path: 'event',
        children: [
          {
            path: '',
            title: 'Plankton events - NPDC',
            component: PlanktonEventListComponent,
          },
          {
            path: ':id',
            resolve: { event: planktonEventResolver },
            component: PlanktonEventItemComponent,
          },
        ],
      },
      {
        path: 'occurrence',
        children: [
          {
            path: '',
            title: 'Plankton occurrences - NPDC',
            component: PlanktonOccurrenceListComponent,
          },
          {
            path: ':id',
            resolve: { occurrence: planktonOccurrenceResolver },
            component: PlanktonOccurrenceItemComponent,
          },
        ],
      },
      {
        path: 'origin',
        canActivate: [isAdminGuard],
        children: [
          {
            path: '',
            title: 'Plankton import batches - NPDC',
            component: PlanktonOriginListComponent,
          },
          {
            path: ':id',
            resolve: { origin: planktonOriginResolver },
            component: PlanktonOriginItemComponent,
          },
        ],
      },
      {
        path: 'taxon',
        children: [
          {
            path: '',
            title: 'Plankton taxa - NPDC',
            component: PlanktonTaxonListComponent,
          },
          {
            path: ':id',
            resolve: { taxon: planktonTaxonResolver },
            component: PlanktonTaxonItemComponent,
          },
        ],
      },
    ],
  },
  {
    path: 'placename',
    children: [
      {
        path: '',
        title: 'Placenames - NPDC',
        component: PlacenameListComponent,
      },
      {
        path: 'new',
        title: 'New placename - NPDC',
        component: PlacenameFormComponent,
        canActivate: [isLoggedInGuard],
      },
      {
        path: 'case',
        children: [
          {
            path: '',
            title: 'Cases - NPDC',
            component: CaseListComponent,
          },
          {
            path: 'new',
            title: 'New case - NPDC',
            component: CaseFormComponent,
            canActivate: [isLoggedInGuard],
          },
          {
            path: ':id',
            children: [
              {
                path: '',
                component: CaseItemComponent,
                resolve: { case: caseResolver },
              },
              {
                path: 'edit',
                component: CaseFormComponent,
                resolve: { case: caseResolver },
                canActivate: [isLoggedInGuard],
              },
            ],
          },
        ],
      },
      {
        path: ':id',
        children: [
          {
            path: '',
            component: PlacenameItemComponent,
            resolve: { placename: placenameResolver },
          },
          {
            path: 'edit',
            component: PlacenameFormComponent,
            resolve: { placename: placenameResolver },
            canActivate: [isLoggedInGuard],
          },
        ],
      },
    ],
  },
  {
    path: 'map',
    children: [
      {
        path: '',
        pathMatch: 'full',
        redirectTo: '/map/archive',
      },
      {
        path: 'archive',
        title: 'Map archive - NPDC',
        children: [
          {
            path: '',
            resolve: { permissions: datasetPermissionResolver },
            component: MapArchiveListComponent,
            data: {
              datasetId: environment.mapArchiveDatasetId,
            },
          },
          {
            path: ':id',
            children: [
              {
                path: '',
                component: MapArchiveItemComponent,
                resolve: {
                  permissions: datasetPermissionResolver,
                  map: mapArchiveResolver,
                },
                data: {
                  datasetId: environment.mapArchiveDatasetId,
                },
              },
              {
                path: 'edit',
                component: MapArchiveStepperComponent,
                canActivate: [isLoggedInGuard],
                resolve: {
                  permissions: datasetPermissionResolver,
                  map: mapArchiveResolver,
                },
                data: {
                  datasetId: environment.mapArchiveDatasetId,
                },
              },
            ],
          },
        ],
      },
    ],
  },
  {
    path: 'vessel',
    title: 'Register of Arctic Vessels',
    children: [
      {
        path: '',
        pathMatch: 'full',
        resolve: { permissions: datasetPermissionResolver },
        data: { datasetId: environment.vesselDatasetId },
        component: VesselListComponent,
      },
      {
        path: ':id',
        children: [
          {
            path: '',
            component: VesselItemComponent,
            resolve: {
              permissions: datasetPermissionResolver,
              vessel: vesselResolver,
            },
            data: { datasetId: environment.vesselDatasetId },
          },
          {
            path: 'edit',
            component: VesselStepperFormComponent,
            resolve: {
              permissions: datasetPermissionResolver,
              vessel: vesselResolver,
            },
            data: { datasetId: environment.vesselDatasetId },
            canActivate: [isLoggedInGuard],
          },
        ],
      },
    ],
  },
  {
    path: 'about',
    title: 'About - NPDC',
    component: AboutComponent,
  },
  {
    path: 'geodata',
    children: [
      {
        path: '',
        title: 'Map data and services - NPDC',
        component: GeodataComponent,
      },
    ],
  },
  {
    path: 'label',
    canActivate: [isAdminGuard],
    children: [
      {
        path: '',
        title: 'Labels - NPDC',
        component: LabelListComponent,
      },
      {
        path: 'new',
        component: LabelFormComponent,
      },
      {
        path: ':id',
        children: [
          {
            path: 'edit',
            component: LabelFormComponent,
            resolve: { label: labelResolver },
          },
        ],
      },
    ],
  },
  {
    path: 'user',
    title: 'Users - NPDC',
    canActivate: [isAdminGuard],
    component: UserListComponent,
  },
  {
    path: 'role',
    canActivate: [isAdminGuard],
    children: [
      {
        path: '',
        title: 'User roles - NPDC',
        component: RoleListComponent,
      },
      {
        path: 'new',
        component: RoleFormComponent,
      },
      {
        path: ':id',
        children: [
          {
            path: 'edit',
            component: RoleFormComponent,
            resolve: { role: roleResolver },
          },
        ],
      },
    ],
  },
  {
    path: 'token',
    canActivate: [isAdminGuard],
    children: [
      {
        path: '',
        title: 'Application tokens - NPDC',
        component: ApplicationListComponent,
      },
      {
        path: 'new',
        component: ApplicationFormComponent,
      },
      {
        path: ':id',
        children: [
          {
            path: 'edit',
            component: ApplicationFormComponent,
            resolve: { application: applicationResolver },
          },
        ],
      },
    ],
  },
  {
    path: 'password/:token',
    title: 'Password - NPDC',
    canMatch: [hasTokenParamGuard],
    canActivate: [isNotLoggedInGuard],
    component: AccountPasswordComponent,
  },
  {
    path: 'kph',
    title: 'RV Kronprins Haakon - NPDC',
    component: KPHLiveComponent,
  },
  {
    path: '**',
    title: '404 - NPDC',
    component: NotFoundComponent,
  },
];

bootstrapApplication(RootComponent, {
  providers: [
    provideBrowserGlobalErrorListeners(),
    provideRouter(
      routes,
      withInMemoryScrolling({
        scrollPositionRestoration: 'top',
        anchorScrolling: 'enabled',
      }),
    ),
    provideNativeDateAdapter(),
    provideHttpClient(withInterceptors([authorizationInterceptor])),
    provideAppInitializer(() => {
      try {
        // @ts-expect-error ScrollWheelZoom is exported by leaflet, but not by @types/leaflet
        const ScrollWheelZoom = Map.ScrollWheelZoom;
        const onWheelScroll = ScrollWheelZoom.prototype._onWheelScroll;
        ScrollWheelZoom.prototype._onWheelScroll = function(e: WheelEvent) {
          if (!e.ctrlKey) {
            return;
          }
          onWheelScroll.call(this, e);
        };
      } catch (err) {
        console.error('failed to patch leaflet scroll', err);
      }
    }),
    { provide: MAT_FORM_FIELD_DEFAULT_OPTIONS, useValue: { subscriptSizing: 'dynamic' } },
    { provide: DATE_PIPE_DEFAULT_OPTIONS, useValue: { dateFormat: 'yyyy-MM-dd' } },
    { provide: MAT_DATE_LOCALE, useValue: 'en-SE' }, // same date format as ISO 8601
  ],
} as ApplicationConfig).catch((err) => console.error(err));
